import React from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Package, ShoppingCart, Users, LogOut, Building2,
  UserCircle, PieChart, FileText, FormInput, RefreshCw, Shield, Menu, X,
  Clock, Inbox, Receipt, MessageCircle, LifeBuoy, Contact, ClipboardCheck
} from 'lucide-react';

const sidebarItems = [
  { id: 'orders', label: 'Orders', icon: ShoppingCart, roles: ['super_admin', 'admin'] },
  { id: 'invoices', label: 'Invoices', icon: Receipt, roles: ['super_admin', 'admin'] },
  { id: 'gst-compliance', label: 'GST Compliance', icon: ClipboardCheck, roles: ['super_admin', 'admin'] },
  { id: 'tickets', label: 'Communication', icon: LifeBuoy, roles: ['super_admin', 'admin'] },
  { id: 'crm', label: 'CRM', icon: Contact, roles: ['super_admin', 'admin'] },
  { id: 'leads', label: 'Leads', icon: Inbox, roles: ['super_admin', 'admin'] },
  { id: 'chatlogs', label: 'Chat Logs', icon: MessageCircle, roles: ['super_admin', 'admin'] },
  { id: 'users', label: 'Users', icon: Users, roles: ['super_admin', 'admin'] },
  { id: 'companies', label: 'Companies', icon: Building2, roles: ['super_admin', 'admin'] },
  { id: 'directors', label: 'Directors', icon: UserCircle, roles: ['super_admin'] },
  { id: 'shareholders', label: 'Shareholders', icon: PieChart, roles: ['super_admin'] },
  { id: 'recurring', label: 'Scheduler', icon: RefreshCw, roles: ['super_admin'] },
  { id: 'expiry-tracker', label: 'Expiry Tracker', icon: Clock, roles: ['super_admin', 'admin'] },
  { id: 'products', label: 'Products', icon: Package, roles: ['super_admin'] },
  { id: 'document-types', label: 'Document Types', icon: FileText, roles: ['super_admin'] },
  { id: 'information-fields', label: 'Info Fields', icon: FormInput, roles: ['super_admin'] },
  { id: 'super-admin', label: 'Super Admin', icon: Shield, roles: ['super_admin'] },
];

export const AdminSidebar = ({ activeTab, setActiveTab, user, onLogout, mobileOpen, setMobileOpen, activityUnreadCount = 0 }) => {
  const userRole = user?.role || 'admin';
  const filteredItems = sidebarItems.filter(item => item.roles.includes(userRole));

  const handleNavClick = (itemId) => {
    setActiveTab(itemId);
    if (setMobileOpen) setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`admin-sidebar ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{
          transition: 'transform 300ms ease-in-out',
        }}
      >
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          {filteredItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            // The unread badge now belongs to Communication (Activity sub-tab lives inside it).
            const showBadge = item.id === 'tickets' && activityUnreadCount > 0;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                data-testid={`admin-nav-${item.id}`}
                className={`admin-nav-item ${isActive ? 'active' : ''}`}
                style={{ position: 'relative' }}
              >
                <Icon />
                <span>{item.label}</span>
                {showBadge && (
                  <span
                    data-testid="admin-nav-activity-badge"
                    style={{
                      marginLeft: 'auto',
                      background: 'var(--admin-danger, #ef4444)',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 10,
                      minWidth: 18,
                      textAlign: 'center',
                    }}
                  >
                    {activityUnreadCount > 99 ? '99+' : activityUnreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: '12px 10px', borderTop: '1px solid var(--admin-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 8px', borderRadius: 8, background: 'var(--admin-bg-elevated)' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--admin-green-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--admin-green)', fontSize: 13, fontWeight: 700 }}>
              {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || 'Admin'}</div>
              <div style={{ fontSize: 11, color: 'var(--admin-text-soft)' }}>{user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="admin-logout-btn"
            className="admin-nav-item"
            style={{ color: 'var(--admin-text-soft)' }}
          >
            <LogOut style={{ width: 16, height: 16 }} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const MobileMenuButton = ({ onClick }) => (
  <button onClick={onClick} style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--admin-text-dim)', display: 'none' }} className="lg-hidden-btn" data-testid="mobile-menu-btn">
    <Menu style={{ width: 20, height: 20 }} />
  </button>
);
